import React, { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Trophy } from "lucide-react";
import { api } from "../lib/api";

const COLS = [
  ["calls", "Calls"],
  ["followups_worked", "Follow-ups worked"],
  ["conversions", "Conversions"],
  ["pool_worked", "Pool worked"],
  ["site_visits_fixed", "SV fixed"],
  ["site_visits_done", "SV done"],
  ["due_today", "Due today"],
  ["pending", "Pending"],
];

export function TeamScorecard({ allowPick = false }) {
  const [leadId, setLeadId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/team/scorecard", { params: leadId ? { lead_id: leadId } : {} })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [leadId]);

  if (!data || (!data.lead && !allowPick)) return null;
  const top = data.rows.reduce((b, r) => (!b || r.followups_worked + r.conversions > b.followups_worked + b.conversions ? r : b), null);

  return (
    <Card className="p-5 space-y-4" data-testid="team-scorecard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-lg font-semibold">Team Scorecard · Today</div>
          <div className="text-xs text-muted-foreground">
            {data.lead ? <>Team of <b>{data.lead.name}</b> · {data.rows.length} members</> : "No Team Leads set up yet — create one in CRM Access."}
          </div>
        </div>
        {allowPick && data.team_leads.length > 0 && (
          <Select value={leadId || data.lead?.id || ""} onValueChange={setLeadId}>
            <SelectTrigger className="w-56" data-testid="team-scorecard-lead-picker"><SelectValue placeholder="Pick a Team Lead" /></SelectTrigger>
            <SelectContent>
              {data.team_leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {data.lead && (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm min-w-[820px]" data-testid="team-scorecard-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                <th className="text-left py-2 pr-3">Member</th>
                {COLS.map(([k, l]) => <th key={k} className="text-right py-2 px-2 font-medium">{l}</th>)}
                <th className="text-right py-2 pl-2">Attendance</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.user_id} className="border-b last:border-0 hover:bg-secondary/40" data-testid={`team-scorecard-row-${r.user_id}`}>
                  <td className="py-2.5 pr-3 font-medium">
                    {r.name}
                    {top && top.user_id === r.user_id && top.followups_worked + top.conversions > 0 && <Trophy className="inline h-3.5 w-3.5 ml-1.5 text-[#B78E4C]" aria-label="Top performer" />}
                  </td>
                  {COLS.map(([k]) => <td key={k} className="py-2.5 px-2 text-right font-mono">{r[k]}</td>)}
                  <td className="py-2.5 pl-2 text-right text-xs">{r.attendance}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={COLS.length + 2} className="py-6 text-center text-muted-foreground text-xs">No members in this team yet.</td></tr>
              )}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot>
                <tr className="font-semibold border-t-2" data-testid="team-scorecard-totals">
                  <td className="py-2.5 pr-3">Team total</td>
                  {COLS.map(([k]) => <td key={k} className="py-2.5 px-2 text-right font-mono">{data.totals[k]}</td>)}
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </Card>
  );
}
